import { useContext, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import ddt from '@wix/da-ddt';
import { MeasuredCookieType, ScreenSizeContext } from '../../Measured';
import { hasCookie } from '../../Measured/redux/selectors';
import { getDeviationType } from '../../DeviationViews/_helpers';
import { DeviationTypes } from '../../types/deviation';
var logger = ddt.createLogger('artstage');
function useIsFirstDeviationAfterSSR(deviation) {
    // this is a bit wacky, but we want to ensure we never recalc the stage for the first deviation after SSR
    // so here we check if this was the first deviation rendered in the component's lifecycle
    // combined with the duperbrowse check, should be Close Enough(tm)
    // we also take note if cookies existed, so important to have this hook run before the cookies get updated
    var isFirstDeviation = useRef(true);
    var firstRenderDeviationId = useRef(deviation.deviationId);
    var hadCookie = useSelector(function (state) {
        return hasCookie(state, MeasuredCookieType.SCREEN_WIDTH);
    });
    var firstDeviationHadCookie = useRef(hadCookie);
    if (isFirstDeviation.current &&
        firstRenderDeviationId.current !== deviation.deviationId) {
        isFirstDeviation.current = false;
    }
    return {
        isFirstDeviation: isFirstDeviation.current,
        hadCookie: firstDeviationHadCookie.current,
    };
}
function calculateFixedStageSize(_a) {
    var screenWidth = _a.width, _b = _a.height, screenHeight = _b === void 0 ? 640 : _b;
    var targetHeight = screenHeight - 280;
    var minHeight = 230;
    var stageHeight = Math.max(targetHeight, minHeight);
    return {
        containerSize: {
            width: screenWidth,
            height: stageHeight,
        },
        contentSize: {
            width: screenWidth,
            height: stageHeight,
        },
    };
}
export function useMobileStage(deviation) {
    var _a = useIsFirstDeviationAfterSSR(deviation), hadCookie = _a.hadCookie, isFirstDeviation = _a.isFirstDeviation;
    var _b = useContext(ScreenSizeContext), screenHeight = _b.height, screenWidth = _b.width;
    var deviationType = getDeviationType(deviation);
    // we do not want to recalculate when the screen height changes because that
    // happens a lot, eg. browser toolbars hiding, keyboard etc. Screen width
    // change will still trigger a recalc which is good enough for stuff like rotation
    var initialScreenHeight = useRef(screenHeight);
    initialScreenHeight.current = screenHeight; // do keep it up to date though
    var stageSize = useMemo(function () {
        // journals and commissions have a hardcoded height
        if ([DeviationTypes.JOURNAL, DeviationTypes.COMMISSION].includes(deviationType)) {
            return {
                containerSize: {
                    width: screenWidth,
                    height: 300,
                },
                contentSize: {
                    width: screenWidth,
                    height: 300,
                },
            };
        }
        // First deviation without cookie must be the default stage size
        if (!hadCookie && isFirstDeviation) {
            logger.log('No cookie');
            return calculateFixedStageSize({ width: screenWidth });
        }
        return calculateFixedStageSize({
            width: screenWidth,
            height: initialScreenHeight.current,
        });
    }, [hadCookie, isFirstDeviation, screenWidth, deviationType]);
    return stageSize;
}
//# sourceMappingURL=useMobileStage.js.map